import React, { useState, useEffect } from 'react';
import { Box, Typography, Container, Grid, CircularProgress, Chip, Dialog } from '@mui/material';
import PageBanner from '../../components/PageBanner';
import SEO from '../../components/SEO';
import SectionSidebar from '../../components/SectionSidebar';
import TerrainIcon from '@mui/icons-material/Terrain';
import LandscapeIcon from '@mui/icons-material/Landscape';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import NearMeIcon from '@mui/icons-material/NearMe';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FilterHdrIcon from '@mui/icons-material/FilterHdr';
import WbTwilightIcon from '@mui/icons-material/WbTwilight';
import HikingIcon from '@mui/icons-material/Hiking';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CloseIcon from '@mui/icons-material/Close';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';

const GOLD = '#d4af37';

interface ViewpointItem {
  id: string;
  name: string;
  distance: string;
  type: string;
  location: string;
  description?: string;
  imageUrl?: string;
  color?: string;
  height?: string;
}

interface ViewpointsContent {
  title: string;
  description: string;
  viewpoints: ViewpointItem[];
}

const accentColors = [
  '#22c55e',
  '#06b6d4',
  '#8b5cf6',
  '#f59e0b',
  '#3b82f6',
  '#ec4899',
];

const tips = [
  { text: 'Visit early morning for the best views and mist', icon: WbTwilightIcon },
  { text: 'Carry binoculars for distant landscape viewing', icon: VisibilityIcon },
  { text: 'Wear sturdy footwear for rocky terrain', icon: HikingIcon },
  { text: 'Bring a light jacket — hilltop temperatures are cooler', icon: AcUnitIcon },
  { text: 'Best season: October to March', icon: CalendarMonthIcon },
  { text: 'Avoid viewpoints during monsoon heavy rains', icon: FilterHdrIcon },
];

export default function Viewpoints() {
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [content, setContent] = useState<ViewpointsContent>({
    title: 'Viewpoints Nearby',
    description: 'Scenic viewpoints around Muchukunnu',
    viewpoints: []
  });

  useEffect(() => {
    try {
      const unsubscribe = onSnapshot(doc(db, 'nearby_content', 'viewpoints'), (docSnap) => {
        if (docSnap.exists()) {
          setContent(docSnap.data() as ViewpointsContent);
        }
        setLoading(false);
      });
      return () => unsubscribe();
    } catch (error) {
      console.error('Error loading viewpoints:', error);
      setLoading(false);
    }
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  const items = content.viewpoints.map((v, i) => ({ ...v, accent: accentColors[i % accentColors.length] }));

  return (
    <Box sx={{ bgcolor: '#f3f8f1', minHeight: '100vh' }}>
      <SEO title="Scenic Viewpoints Near Kottayil Temple | വ്യൂ പോയിന്റുകൾ" description="Visit scenic viewpoints and nature spots near Kottayil Temple (Kotta-Kovilakam Kshethram) in Kozhikode, Kerala. Hilltops, waterfalls, and nature trails." path="/nearby/viewpoints" keywords="scenic viewpoints near temple, Kozhikode viewpoints, Kerala nature spots, hilltops Kozhikode, കോഴിക്കോട് വ്യൂ പോയിന്റ്, nature spots near Muchukunnu" />
      <PageBanner title="Scenic Viewpoints" />
      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2, pb: 10 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8} lg={9}>

            {/* HERO */}
            <Box
              sx={{
                borderRadius: '30px',
                overflow: 'hidden',
                mb: 4,
                boxShadow: '0 22px 64px rgba(22, 71, 29, 0.2)',
                background: 'linear-gradient(145deg, #174728 0%, #256438 48%, #3f9d52 100%)',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  opacity: 0.13,
                  background:
                    'repeating-linear-gradient(120deg, rgba(255,255,255,0.18) 0px, rgba(255,255,255,0.18) 2px, transparent 2px, transparent 14px)',
                }}
              />
              <Box sx={{ position: 'absolute', top: -56, right: -56, width: 240, height: 240, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.16) 0%, transparent 70%)' }} />
              <Box sx={{ position: 'absolute', bottom: -48, left: -48, width: 190, height: 190, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.12) 0%, transparent 72%)' }} />

              <Box sx={{ px: { xs: 3, sm: 4 }, py: { xs: 4, sm: 5 }, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2.5, flexWrap: 'wrap' }}>
                  <Box
                    sx={{
                      width: 66,
                      height: 66,
                      borderRadius: '20px',
                      background: 'rgba(255,255,255,0.2)',
                      backdropFilter: 'blur(10px)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid rgba(255,255,255,0.25)',
                      boxShadow: '0 12px 30px rgba(14,46,21,0.3)',
                    }}
                  >
                    <TerrainIcon sx={{ fontSize: 32, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.6rem', sm: '2rem' }, color: '#fff', lineHeight: 1.15, mb: 0.5, textShadow: '0 2px 12px rgba(0,0,0,0.22)' }}>
                      Scenic Viewpoints Nearby
                    </Typography>
                    <Typography sx={{ fontSize: { xs: '0.82rem', sm: '0.9rem' }, color: 'rgba(255,255,255,0.68)', fontWeight: 500 }}>
                      Hilltops, valleys, and panoramic nature spots
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: { xs: '0.88rem', sm: '0.95rem' }, color: 'rgba(255,255,255,0.58)', lineHeight: 1.9, maxWidth: 560, mb: 2.5 }}>
                  {content.description || 'Discover stunning viewpoints offering panoramic views of North Kerala\'s lush green landscapes, valleys, and distant coastline.'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2.2 }}>
                  <Chip icon={<FilterHdrIcon sx={{ fontSize: '16px !important', color: '#216b2c !important' }} />} label={`${items.length} Viewpoint${items.length !== 1 ? 's' : ''}`} sx={{ bgcolor: 'rgba(255,255,255,0.95)', color: '#1d5726', fontWeight: 700, fontSize: '0.78rem', height: 34, borderRadius: '12px', '& .MuiChip-icon': { ml: '8px' } }} />
                  <Chip icon={<VisibilityIcon sx={{ fontSize: '16px !important', color: '#86efac !important' }} />} label="Panoramic Vistas" sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.92)', fontWeight: 700, fontSize: '0.78rem', height: 34, borderRadius: '12px', border: '1px solid rgba(255,255,255,0.18)', '& .MuiChip-icon': { ml: '8px' } }} />
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0,1fr))' }, gap: 1.3 }}>
                  {[
                    { label: 'Viewpoint Directory', value: `${items.length}`, icon: TerrainIcon },
                    { label: 'Best Time', value: 'Sunrise & Sunset', icon: WbTwilightIcon },
                    { label: 'Trail Friendly', value: 'Walk & Hike Ready', icon: HikingIcon },
                  ].map((metric) => {
                    const MIcon = metric.icon;
                    return (
                      <Box key={metric.label} sx={{ borderRadius: '14px', p: 1.4, bgcolor: 'rgba(255,255,255,0.13)', border: '1px solid rgba(255,255,255,0.16)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1 }}>
                          <Box sx={{ width: 28, height: 28, borderRadius: '9px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <MIcon sx={{ fontSize: 16, color: '#fff' }} />
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ color: '#fff', fontSize: '0.8rem', fontWeight: 700, lineHeight: 1.2 }}>{metric.value}</Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.74)', fontSize: '0.7rem', lineHeight: 1.2 }}>{metric.label}</Typography>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>

            {/* VIEWPOINT CARDS */}
            {items.length === 0 ? (
              <Box sx={{ bgcolor: '#fff', borderRadius: '24px', p: 6, textAlign: 'center', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
                <LandscapeIcon sx={{ fontSize: 56, color: '#e0e0e0', mb: 2 }} />
                <Typography sx={{ fontSize: { xs: '0.88rem', md: '1rem' }, color: '#bbb', fontWeight: 600 }}>Viewpoint information coming soon.</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5, mb: 4 }}>
                {items.map((v, idx) => {
                  const hasImage = !!v.imageUrl;
                  const isEven = idx % 2 === 0;

                  return (
                    <Box key={v.id || v.name} sx={{
                      bgcolor: '#fff', borderRadius: '24px', overflow: 'hidden',
                      border: '1px solid rgba(32,93,40,0.14)',
                      boxShadow: '0 4px 24px rgba(23,77,34,0.1)',
                      transition: 'all 400ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 20px 60px rgba(23,77,34,0.18), 0 0 0 1px ${v.accent}30`,
                      },
                    }}>
                      {/* Card with image + content side by side */}
                      <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: isEven ? 'row' : 'row-reverse' },
                      }}>
                        {/* Image section */}
                        {hasImage ? (
                          <Box
                            onClick={() => setLightbox(v.imageUrl || null)}
                            sx={{
                              width: { xs: '100%', sm: '45%' },
                              minHeight: { xs: 220, sm: 280 },
                              position: 'relative',
                              cursor: 'pointer',
                              overflow: 'hidden',
                              flexShrink: 0,
                              '&:hover .img-overlay': { opacity: 1 },
                              '&:hover img': { transform: 'scale(1.05)' },
                            }}
                          >
                            <img
                              src={v.imageUrl}
                              alt={v.name}
                              style={{
                                width: '100%', height: '100%',
                                objectFit: 'cover', display: 'block',
                                position: 'absolute', inset: 0,
                                transition: 'transform 600ms ease',
                              }}
                            />
                            {/* hover overlay */}
                            <Box className="img-overlay" sx={{
                              position: 'absolute', inset: 0,
                              bgcolor: 'rgba(0,0,0,0.25)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              opacity: 0, transition: 'opacity 300ms',
                            }}>
                              <Box sx={{
                                width: 48, height: 48, borderRadius: '50%',
                                bgcolor: 'rgba(255,255,255,0.9)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}>
                                <PhotoCameraIcon sx={{ fontSize: 22, color: '#333' }} />
                              </Box>
                            </Box>
                          </Box>
                        ) : (
                          /* Gradient placeholder when no image */
                          <Box sx={{
                            width: { xs: '100%', sm: '45%' },
                            minHeight: { xs: 180, sm: 280 },
                            background: `linear-gradient(135deg, ${v.accent}18 0%, ${v.accent}08 100%)`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0,
                          }}>
                            <TerrainIcon sx={{ fontSize: 64, color: `${v.accent}40` }} />
                          </Box>
                        )}

                        {/* Content section */}
                        <Box sx={{
                          flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center',
                          px: { xs: 2.5, sm: 3.5 }, py: { xs: 2.5, sm: 3 }, bgcolor: '#fbfff8',
                        }}>
                          {/* Name with accent bar */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                            <Box sx={{
                              width: 4, height: 28, borderRadius: '4px',
                              background: `linear-gradient(180deg, ${v.accent}, ${v.accent}80)`,
                              flexShrink: 0,
                            }} />
                            <Typography sx={{
                              fontWeight: 800, fontSize: { xs: '1.15rem', sm: '1.3rem' },
                              color: '#1f3a24', lineHeight: 1.25, letterSpacing: '-0.01em',
                            }}>
                              {v.name}
                            </Typography>
                          </Box>

                          {/* Location */}
                          {v.location && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 1.5, ml: '20px' }}>
                              <LocationOnIcon sx={{ fontSize: 15, color: '#7f987e' }} />
                              <Typography sx={{ fontSize: '0.82rem', color: '#7f987e', fontWeight: 500 }}>
                                {v.location}
                              </Typography>
                            </Box>
                          )}

                          {/* Tags */}
                          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                            {v.distance && (
                              <Chip
                                icon={<NearMeIcon sx={{ fontSize: '14px !important', color: `${v.accent} !important` }} />}
                                label={v.distance}
                                size="small"
                                sx={{
                                  bgcolor: `${v.accent}10`, color: v.accent, fontWeight: 700,
                                  fontSize: '0.74rem', height: 28, borderRadius: '8px',
                                  border: `1px solid ${v.accent}20`,
                                  '& .MuiChip-icon': { ml: '6px' },
                                }}
                              />
                            )}
                            {v.type && (
                              <Chip
                                icon={<TerrainIcon sx={{ fontSize: '14px !important', color: '#64748b !important' }} />}
                                label={v.type}
                                size="small"
                                sx={{
                                  bgcolor: 'rgba(100,116,139,0.07)', color: '#64748b', fontWeight: 600,
                                  fontSize: '0.74rem', height: 28, borderRadius: '8px',
                                  border: '1px solid rgba(100,116,139,0.1)',
                                  '& .MuiChip-icon': { ml: '6px' },
                                }}
                              />
                            )}
                          </Box>

                          {/* Description */}
                          {v.description && (
                            <Typography sx={{
                              fontSize: { xs: '0.85rem', sm: '0.9rem' },
                              color: '#536b52', lineHeight: 1.85,
                              letterSpacing: '0.01em',
                              whiteSpace: 'pre-line',
                            }}>
                              {v.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}

            {/* TIPS FOOTER */}
            <Box sx={{
              background: 'linear-gradient(145deg, #16311a 0%, #224928 45%, #112214 100%)',
              borderRadius: '24px', p: { xs: 3, sm: 3.5 }, position: 'relative', overflow: 'hidden',
              border: '1px solid rgba(255,255,255,0.06)', boxShadow: '0 8px 32px rgba(0,0,0,0.16)',
            }}>
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.06, backgroundImage: 'linear-gradient(115deg, rgba(255,255,255,0.15) 0 2px, transparent 2px 12px)' }} />
              <Box sx={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: `radial-gradient(circle, ${GOLD}18 0%, transparent 70%)` }} />
              <Box sx={{ position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '12px', background: `linear-gradient(135deg, ${GOLD}, #b8941f)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 6px 20px ${GOLD}30` }}>
                    <TipsAndUpdatesIcon sx={{ fontSize: 20, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.88rem', md: '1rem' }, color: '#fff', lineHeight: 1.2 }}>Visitor Tips</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)', fontWeight: 500 }}>Make the most of your visit</Typography>
                  </Box>
                </Box>
                <Grid container spacing={1.5}>
                  {tips.map((t, i) => {
                    const TIcon = t.icon;
                    return (
                      <Grid item xs={12} sm={6} key={i}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, px: 1.5, borderRadius: '10px', transition: 'all 250ms', border: '1px solid rgba(255,255,255,0.06)', bgcolor: 'rgba(255,255,255,0.02)', '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}>
                          <Box sx={{ width: 24, height: 24, borderRadius: '7px', bgcolor: `${GOLD}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <TIcon sx={{ fontSize: 13, color: GOLD }} />
                          </Box>
                          <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.75)', fontWeight: 500, lineHeight: 1.6 }}>{t.text}</Typography>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Box>

          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <SectionSidebar sectionKey="nearby" activePath="/nearby/viewpoints" />
          </Grid>
        </Grid>
      </Container>

      {/* LIGHTBOX */}
      <Dialog
        open={!!lightbox}
        onClose={() => setLightbox(null)}
        maxWidth={false}
        PaperProps={{
          sx: {
            bgcolor: 'transparent', boxShadow: 'none', m: 0,
            maxWidth: { xs: '95vw', sm: '90vw' }, maxHeight: '85dvh', overflow: 'visible',
          }
        }}
        slotProps={{ backdrop: { sx: { bgcolor: 'rgba(0,0,0,0.88)' } } }}
      >
        <Box sx={{ position: 'relative' }}>
          <Box
            onClick={() => setLightbox(null)}
            sx={{
              position: 'absolute', top: -16, right: -16, zIndex: 10,
              width: 36, height: 36, borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.9)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              '&:hover': { bgcolor: '#fff' },
            }}
          >
            <CloseIcon sx={{ fontSize: 18, color: '#333' }} />
          </Box>
          {lightbox && (
            <img
              src={lightbox}
              alt="Viewpoint"
              style={{
                maxWidth: '90vw', maxHeight: '85dvh',
                borderRadius: '16px', display: 'block',
                objectFit: 'contain',
              }}
            />
          )}
        </Box>
      </Dialog>
    </Box>
  );
}
